// ===========================================
// Invitation React Query Hooks
// ===========================================
// Custom hooks for parent and student invitation operations

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  invitationsApi,
  studentInvitationsApi,
  publicInvitationsApi,
  InvitationStatusResponse,
  StudentInvitationStatusResponse,
  InvitationType,
} from '../api/invitations.api';
import { userKeys } from './useUsers';

type ApiError = Error & { response?: { data?: { message?: string } } };

// ===========================================
// QUERY KEYS
// ===========================================

export const invitationKeys = {
  all: ['invitations'] as const,
  status: (parentId: string) => [...invitationKeys.all, 'status', parentId] as const,
  studentStatus: (studentId: string) => [...invitationKeys.all, 'student-status', studentId] as const,
  validate: (token: string) => [...invitationKeys.all, 'validate', token] as const,
};

// ===========================================
// AUTHENTICATED HOOKS
// ===========================================

/**
 * Get invitation status for a parent's contact2
 * Use this to show invitation status and available actions in the UI
 */
export function useContact2InvitationStatus(parentId: string | null) {
  return useQuery({
    queryKey: invitationKeys.status(parentId || ''),
    queryFn: () => invitationsApi.getStatus(parentId!),
    enabled: !!parentId,
    select: (response) => response as InvitationStatusResponse,
  });
}

/**
 * Send an invitation to contact2
 */
export function useSendContact2Invitation() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (parentId: string) => invitationsApi.sendInvitation(parentId),
    onSuccess: (_, parentId) => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.status(parentId) });
      queryClient.invalidateQueries({ queryKey: userKeys.parent(parentId) });
      enqueueSnackbar('Invitation sent', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      enqueueSnackbar(error.response?.data?.message || 'Failed to send invitation', {
        variant: 'error',
      });
    },
  });
}

/**
 * Resend an invitation to contact2
 */
export function useResendContact2Invitation() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (parentId: string) => invitationsApi.resendInvitation(parentId),
    onSuccess: (_, parentId) => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.status(parentId) });
      enqueueSnackbar('Invitation resent', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      enqueueSnackbar(error.response?.data?.message || 'Failed to resend invitation', {
        variant: 'error',
      });
    },
  });
}

/**
 * Cancel a pending invitation
 */
export function useCancelContact2Invitation() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (parentId: string) => invitationsApi.cancelInvitation(parentId),
    onSuccess: (_, parentId) => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.status(parentId) });
      enqueueSnackbar('Invitation cancelled', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      enqueueSnackbar(error.response?.data?.message || 'Failed to cancel invitation', {
        variant: 'error',
      });
    },
  });
}

// ===========================================
// PUBLIC HOOKS (For Accept Invitation Page)
// ===========================================

/**
 * Validate an invitation token
 * Use this on the accept invitation page to verify the token is valid
 */
export function useValidateInvitationToken(
  token: string | undefined,
  type?: InvitationType
) {
  return useQuery({
    queryKey: invitationKeys.validate(token || ''),
    queryFn: () => publicInvitationsApi.validateToken(token!, type),
    enabled: !!token,
    retry: false, // Don't retry on invalid tokens
  });
}

/**
 * Accept an invitation and create account
 */
export function useAcceptInvitation() {
  return useMutation({
    mutationFn: ({
      token,
      password,
      type,
      agreement,
    }: {
      token: string;
      password: string;
      type?: InvitationType;
      /** #442 D1c: a parent invitation's T&C + Privacy ticks. */
      agreement?: { termsAccepted: boolean; privacyAccepted: boolean };
    }) => publicInvitationsApi.acceptInvitation(token, password, type, agreement),
  });
}

// ===========================================
// STUDENT INVITATION HOOKS (Admin/Parent)
// ===========================================

/**
 * Get invitation status for a student
 * Use this to show invitation status and available actions in the UI
 */
export function useStudentInvitationStatus(studentId: string | null) {
  return useQuery({
    queryKey: invitationKeys.studentStatus(studentId || ''),
    queryFn: () => studentInvitationsApi.getStatus(studentId!),
    enabled: !!studentId,
    select: (response) => response as StudentInvitationStatusResponse,
  });
}

/**
 * Send an invitation to a student
 */
export function useSendStudentInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (studentId: string) => studentInvitationsApi.sendInvitation(studentId),
    onSuccess: (_, studentId) => {
      // Invalidate the invitation status for this student
      queryClient.invalidateQueries({ queryKey: invitationKeys.studentStatus(studentId) });
    },
  });
}

/**
 * Resend an invitation to a student
 */
export function useResendStudentInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (studentId: string) => studentInvitationsApi.resendInvitation(studentId),
    onSuccess: (_, studentId) => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.studentStatus(studentId) });
    },
  });
}

/**
 * Cancel a pending student invitation
 */
export function useCancelStudentInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (studentId: string) => studentInvitationsApi.cancelInvitation(studentId),
    onSuccess: (_, studentId) => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.studentStatus(studentId) });
    },
  });
}
